import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  People as PeopleIcon,
} from '@mui/icons-material';
import { Patient, CreatePatientRequest } from '../types/Patient';
import { patientService } from '../services/patientService';
import { formatDate } from '../utils/formatters';

const PatientsPage: React.FC = () => {
  const [patients, setPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingPatient, setEditingPatient] = useState<Patient | null>(null);
  const [formData, setFormData] = useState<CreatePatientRequest>({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    dateOfBirth: '',
    gender: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    emergencyContactName: '',
    emergencyContactPhone: '',
    bloodType: '',
    allergies: '',
    currentMedications: '',
  });
  const [ageInput, setAgeInput] = useState<string>('');
  const [registrationDate, setRegistrationDate] = useState<string>(new Date().toISOString().split('T')[0]);

  const bloodTypes = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
  const genders = ['Male', 'Female', 'Other'];

  useEffect(() => {
    fetchPatients();
  }, []);

  const fetchPatients = async () => {
    try {
      setLoading(true);
      const data = await patientService.getAllPatients();
      setPatients(data);
    } catch (error) {
      console.error('Failed to fetch patients:', error);
      setError('Failed to load patients');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      fetchPatients();
      return;
    }

    try {
      setLoading(true);
      const data = await patientService.searchPatients(searchTerm);
      setPatients(data);
    } catch (error) {
      console.error('Failed to search patients:', error);
      setError('Failed to search patients');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      // Convert age to approximate date of birth
      const submitData = { ...formData };
      if (ageInput) {
        const age = parseInt(ageInput, 10);
        if (!isNaN(age) && age >= 0) {
          const today = new Date();
          const birthYear = today.getFullYear() - age;
          submitData.dateOfBirth = new Date(birthYear, 0, 1).toISOString().split('T')[0];
        }
      }
      if (editingPatient) {
        await patientService.updatePatient(editingPatient.patientId, submitData);
      } else {
        submitData.registrationDate = registrationDate || new Date().toISOString().split('T')[0];
        await patientService.createPatient(submitData);
      }
      await fetchPatients();
      handleCloseDialog();
    } catch (error) {
      console.error('Failed to save patient:', error);
      setError('Failed to save patient');
    }
  };

  const handleDelete = async (patientId: number) => {
    if (window.confirm('Are you sure you want to delete this patient?')) {
      try {
        await patientService.deletePatient(patientId);
        await fetchPatients();
      } catch (error) {
        console.error('Failed to delete patient:', error);
        setError('Failed to delete patient');
      }
    }
  };

  const handleOpenDialog = (patient?: Patient) => {
    if (patient) {
      setEditingPatient(patient);
      setFormData({
        firstName: patient.firstName,
        lastName: patient.lastName,
        email: patient.email,
        phoneNumber: patient.phoneNumber,
        dateOfBirth: patient.dateOfBirth ? patient.dateOfBirth.split('T')[0] : '',
        gender: patient.gender,
        address: patient.address,
        city: patient.city,
        state: patient.state,
        zipCode: patient.zipCode,
        emergencyContactName: patient.emergencyContactName,
        emergencyContactPhone: patient.emergencyContactPhone,
        bloodType: patient.bloodType,
        allergies: patient.allergies || '',
        currentMedications: patient.currentMedications || '',
      });
      setAgeInput(patient.age ? String(patient.age) : (patient.dateOfBirth ? String(getAgeFromBirthDate(patient.dateOfBirth)) : ''));
    } else {
      setEditingPatient(null);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        dateOfBirth: '',
        gender: '',
        address: '',
        city: '',
        state: '',
        zipCode: '',
        emergencyContactName: '',
        emergencyContactPhone: '',
        bloodType: '',
        allergies: '',
        currentMedications: '',
      });
      setAgeInput('');
      setRegistrationDate(new Date().toISOString().split('T')[0]);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingPatient(null);
  };

  const handleInputChange = (field: keyof CreatePatientRequest, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const getAgeFromBirthDate = (birthDate: string | null | undefined) => {
    if (!birthDate) return null;
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    
    return age;
  };

  const filteredPatients = patients.filter(patient =>
    patient.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    patient.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    patient.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="400px"
        sx={{ 
          background: 'linear-gradient(135deg, #f8fffe 0%, #e0f2f1 100%)',
          borderRadius: 3,
          m: 2
        }}
      >
        <CircularProgress sx={{ color: '#2C7A9C' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      p: 3, 
      minHeight: '100vh', 
      background: 'transparent',
    }}>
      {/* Header Card */}
      <Card 
        elevation={0}
        sx={{
          background: 'linear-gradient(135deg, #2C7A9C 0%, #1E5A73 100%)',
          color: 'white',
          mb: 4,
          borderRadius: 3,
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <PeopleIcon sx={{ fontSize: 48, color: 'white' }} />
              <Box>
                <Typography variant="h3" component="h1" sx={{ fontWeight: 700, mb: 1, color: 'white' }}>
                  Patient Management
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9, color: 'white' }}>
                  Register patients, manage medical records, and track health information
                </Typography>
              </Box>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              sx={{
                bgcolor: 'rgba(255,255,255,0.2)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255,255,255,0.3)',
                color: 'white',
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                py: 1.5,
                borderRadius: 2,
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.3)',
                }
              }}
            >
              Add Patient
            </Button>
          </Box>
        </CardContent>
      </Card>

      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 3, borderRadius: 2 }}
          onClose={() => setError('')}
        >
          {error}
        </Alert>
      )}

      {/* Search Card */}
      <Card 
        elevation={0}
        sx={{
          mb: 3,
          borderRadius: 3,
          border: '1px solid #e2e8f0',
          background: 'rgba(255,255,255,0.8)',
          backdropFilter: 'blur(10px)',
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              placeholder="Search by name or email..."
              variant="outlined"
              fullWidth
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'white',
                  borderRadius: 2,
                  '& fieldset': {
                    borderColor: '#e2e8f0',
                  },
                  '&:hover fieldset': {
                    borderColor: '#2C7A9C',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#2C7A9C',
                  },
                },
              }}
            />
            <Button
              variant="contained"
              onClick={handleSearch}
              startIcon={<SearchIcon />}
              sx={{
                background: 'linear-gradient(135deg, #2C7A9C 0%, #1E5A73 100%)',
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                py: 1.5,
                borderRadius: 2,
                '&:hover': {
                  background: 'linear-gradient(135deg, #1E5A73 0%, #134152 100%)',
                }
              }}
            >
              Search
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Patients Table */}
      <Card 
        elevation={0}
        sx={{
          borderRadius: 3,
          border: '1px solid #e2e8f0',
          background: 'rgba(255,255,255,0.9)',
          backdropFilter: 'blur(10px)',
          overflow: 'hidden',
        }}
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ background: 'linear-gradient(135deg, #2C7A9C 0%, #1E5A73 100%)' }}>
                <TableCell sx={{ color: 'white', fontWeight: 700, py: 2 }}>Name</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 700, py: 2 }}>Age</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 700, py: 2 }}>Gender</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 700, py: 2 }}>Contact</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 700, py: 2 }}>Blood Type</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 700, py: 2 }}>Registration Date</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 700, py: 2 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredPatients.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                    <Typography variant="h6" sx={{ color: '#64748b', mb: 1 }}>
                      No patients found
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748b' }}>
                      {searchTerm ? 'Try adjusting your search criteria' : 'Start by adding your first patient'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredPatients.map((patient, index) => (
                  <TableRow 
                    key={patient.patientId}
                    sx={{
                      backgroundColor: index % 2 === 0 ? '#f8fafc' : 'white',
                      '&:hover': {
                        backgroundColor: '#f0f9ff',
                      },
                    }}
                  >
                    <TableCell sx={{ py: 2 }}>
                      <Box>
                        <Typography variant="body1" fontWeight="bold" sx={{ color: '#1E5A73' }}>
                          {patient.firstName} {patient.lastName}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#64748b' }}>
                          {patient.email}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 2, color: '#374151' }}>
                      {patient.dateOfBirth ? `${getAgeFromBirthDate(patient.dateOfBirth)} years` : '-'}
                    </TableCell>
                    <TableCell sx={{ py: 2, color: '#374151' }}>{patient.gender}</TableCell>
                    <TableCell sx={{ py: 2, color: '#374151' }}>{patient.phoneNumber}</TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Chip 
                        label={patient.bloodType} 
                        size="small"
                        sx={{
                          backgroundColor: '#fee2e2',
                          color: '#dc2626',
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 2, color: '#374151' }}>
                      {formatDate(patient.patientAddedDate ?? patient.createdDate)}
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Tooltip title="Edit Patient">
                        <IconButton
                          sx={{ 
                            color: '#2C7A9C',
                            '&:hover': { backgroundColor: '#e8f4f8' },
                          }}
                          size="small"
                          onClick={() => handleOpenDialog(patient)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Patient">
                        <IconButton
                          sx={{ 
                            color: '#dc2626',
                            '&:hover': { backgroundColor: '#fee2e2' },
                          }}
                          size="small"
                          onClick={() => handleDelete(patient.patientId)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Add/Edit Patient Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: 3,
            background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
          }
        }}
      >
        <DialogTitle 
          sx={{ 
            background: 'linear-gradient(135deg, #2C7A9C 0%, #1E5A73 100%)',
            color: 'white',
            fontWeight: 700,
          }}
        >
          {editingPatient ? 'Edit Patient' : 'Add New Patient'}
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Grid container spacing={3} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                value={formData.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    borderRadius: 2,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={formData.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    borderRadius: 2,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    borderRadius: 2,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone Number"
                value={formData.phoneNumber}
                onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    borderRadius: 2,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Age"
                type="number"
                value={ageInput}
                onChange={(e) => setAgeInput(e.target.value)}
                inputProps={{ min: 0, max: 150 }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    borderRadius: 2,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl 
                fullWidth 
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    borderRadius: 2,
                  },
                }}
              >
                <InputLabel>Gender</InputLabel>
                <Select
                  value={formData.gender}
                  onChange={(e) => handleInputChange('gender', e.target.value)}
                  label="Gender"
                >
                  {genders.map((gender) => (
                    <MenuItem key={gender} value={gender}>
                      {gender}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {!editingPatient && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Registration Date"
                  type="date"
                  value={registrationDate}
                  onChange={(e) => setRegistrationDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'white',
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                value={formData.address}
                onChange={(e) => handleInputChange('address', e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    borderRadius: 2,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="City"
                value={formData.city}
                onChange={(e) => handleInputChange('city', e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    borderRadius: 2,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="State"
                value={formData.state}
                onChange={(e) => handleInputChange('state', e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    borderRadius: 2,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="ZIP Code"
                value={formData.zipCode}
                onChange={(e) => handleInputChange('zipCode', e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    borderRadius: 2,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Emergency Contact Name"
                value={formData.emergencyContactName}
                onChange={(e) => handleInputChange('emergencyContactName', e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    borderRadius: 2,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Emergency Contact Phone"
                value={formData.emergencyContactPhone}
                onChange={(e) => handleInputChange('emergencyContactPhone', e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    borderRadius: 2,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl 
                fullWidth 
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    borderRadius: 2,
                  },
                }}
              >
                <InputLabel>Blood Type</InputLabel>
                <Select
                  value={formData.bloodType}
                  onChange={(e) => handleInputChange('bloodType', e.target.value)}
                  label="Blood Type"
                >
                  {bloodTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Allergies"
                value={formData.allergies}
                onChange={(e) => handleInputChange('allergies', e.target.value)}
                multiline
                rows={2}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    borderRadius: 2,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Current Medications"
                value={formData.currentMedications}
                onChange={(e) => handleInputChange('currentMedications', e.target.value)}
                multiline
                rows={3}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    borderRadius: 2,
                  },
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button 
            onClick={handleCloseDialog}
            sx={{ 
              color: '#64748b',
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #2C7A9C 0%, #1E5A73 100%)',
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              '&:hover': {
                background: 'linear-gradient(135deg, #1E5A73 0%, #134152 100%)',
              }
            }}
          >
            {editingPatient ? 'Update Patient' : 'Add Patient'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PatientsPage;